import { QUERY_STRING_INSTALL_PAGE_URL, QUERY_STRING_INSTALL_TYPE } from "../../constants";
import { iframeConstants as constants, iframeTitle } from "./constants";
import { InstallPageEvent } from "../frames/frameMessages";
import type { HookEvents, Options, PostMessageAction, ViewModel } from "../types/types";
import { logger } from "../utils/logger/logger";
import { getVerificationUrl } from "./utils";
import { EventEmitter } from "../hooks/eventEmitter";

export type IframeOptions = {
  className: string;
  title: string;
};

export const DEFAULT_OPTIONS: IframeOptions = {
  className: constants.CLASS_NAMES.INLINE_IFRAME_CONTENT,
  title: iframeTitle,
};

export const assertValidFunction = (candidate: Function) => {
  if (typeof candidate !== "function") {
    throw new Error(`Expected type "function", but received ${typeof candidate}`);
  }
};

class Iframe extends EventEmitter<HookEvents> {
  containerElement: HTMLElement;

  verificationUrl: URL;

  isMobileDevice: boolean;

  iframe: HTMLIFrameElement;

  verificationIframeUid: string;

  locale: string;

  hasLoaded: boolean = false;

  onLoadEvents: Function[] = [];

  onCleanupEvents: (() => void)[] = [];

  installType = "cdn_inline_iframe";

  constructor(containerElement: HTMLElement, url: string) {
    super();

    if (Iframe.isValidHttpUrl(url)) {
      this.containerElement = containerElement;
      this.verificationUrl = new URL(getVerificationUrl(url));
      this.verificationIframeUid = Iframe.createUniqueId();
    } else {
      logger.error("Invalid URL. Provide a proper URL: https://example.com/", "iframe url");
    }
  }

  cleanup(): void {
    this.onCleanupEvents.forEach((callback) => callback());
  }

  static createUniqueId(): string {
    return Math.random().toString(36).substr(2, 9);
  }

  static isValidHttpUrl(urlString: string): boolean {
    try {
      const url = new URL(urlString);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch {
      return false;
    }
  }

  createIframe(options: IframeOptions): HTMLIFrameElement {
    this.iframe = document.createElement("iframe");
    this.iframe.classList.add(options.className);
    this.iframe.title = options.title;
    this.iframe.src = this.getIframeUrl();
    this.iframe.allow = "camera *;microphone *;geolocation *;clipboard-read *;clipboard-write *";
    this.iframe.onload = () => this.onLoad();
    return this.iframe;
  }

  getIframeUrl(): string {
    const url = new URL(this.verificationUrl);
    url.searchParams.set("verificationIframeUid", this.verificationIframeUid);
    url.searchParams.set(QUERY_STRING_INSTALL_PAGE_URL, window.location.href);
    url.searchParams.set(QUERY_STRING_INSTALL_TYPE, this.installType);
    if (this.locale) {
      url.searchParams.set("locale", this.locale);
    }
    return url.toString();
  }

  // If iFrame hasn't loaded yet, queue up the callback
  // otherwise call it immediately
  addOnLoadEvent(callback: Function) {
    try {
      assertValidFunction(callback);
    } catch (e) {
      logger.error("Invalid callback provided to iFrame.onLoad()", "iframe addOnLoadEvent");
      return;
    }
    if (this.hasLoaded) {
      callback();
    } else {
      this.onLoadEvents.push(callback);
    }
  }

  onLoad() {
    this.hasLoaded = true;
    this.onLoadEvents.forEach((callback) => callback());
  }

  /**
   * Using this to add parent window message listeners gives us
   * - Event Cleanup for modals
   * - verification of origin, verificationIframeUid, and message data structure
   */
  addWindowMessageListener(actionCb: (action: PostMessageAction) => void): void {
    const listener = (ev: MessageEvent) => {
      if (this.verificationUrl.origin !== ev.origin) {
        return;
      }

      if (ev.data.verificationIframeUid !== this.verificationIframeUid) {
        return;
      }

      if (ev.data.action && ev.data.action.type) {
        actionCb(ev.data.action);
      } else if (ev.data.action && ev.data.height) {
        // Older versions of the jslib send the full action object as the message
        actionCb(ev.data);
      }
    };

    window.addEventListener("message", listener);
    this.onCleanupEvents.push(() => window.removeEventListener("message", listener));
  }

  addActionListener() {
    this.addWindowMessageListener((messageAction) => {
      if (messageAction.type === "hook") {
        this.emit(messageAction.hook.name, messageAction.hook.data);
      }

      if (
        (messageAction.action && messageAction.action === "updateHeight") ||
        messageAction.type === "updateHeight"
      ) {
        // UX-1129: If we are getting height updates, turn off scrolling for
        // the iframe. This prevents browsers (Safari in particular) from
        // re-wrapping because of different widths and constantly changing
        // it's own height. `scrolling = "no"` is deprecated, but supported in
        // all browsers and fixes the problem well.
        this.iframe.scrolling = "no";
        this.iframe.style.height = `${messageAction.height}px`;
      }
    });
  }

  setViewModel(viewModel: Partial<ViewModel> | ViewModel) {
    const message: InstallPageEvent = {
      action: "setViewModel",
      viewModel,
    };

    this.addOnLoadEvent(() => {
      this.iframe.contentWindow.postMessage(message, this.verificationUrl.origin);
    });
  }

  setOptions(options: Options) {
    const message: InstallPageEvent = {
      action: "setOptions",
      options,
    };

    // HC-1181: Special casing options.locale to set locale as as QSP if the frame hasn't fully loaded yet.
    // This causes a re-load, but in most cases would happen within milliseconds of the frame initialization.
    // It prevents the race conditions that were failing to set the locale properly on load.
    // If the associated playwright tests passes without this is can be removed.
    if (!this.hasLoaded && options.locale) {
      this.locale = options.locale;
      const newUrl = this.getIframeUrl();
      if (newUrl !== this.iframe.src) {
        this.iframe.src = newUrl;
      }
    }

    this.addOnLoadEvent(() => {
      this.iframe.contentWindow.postMessage(message, this.verificationUrl.origin);
    });
  }

  setInstallType(installType: string) {
    this.installType = installType;
  }

  init(createIframe: boolean = true): void {
    if (createIframe) {
      this.createIframe(DEFAULT_OPTIONS);
    }

    this.addActionListener();
    this.containerElement.appendChild(this.iframe);
  }
}

export default Iframe;
